import type * as pf from '@amirada/lib-shared'
import { NodeType, PfCmdType } from '@amirada/lib-shared'
import type * as tp from '@base/utils/types'
import { ut_capStore, ut_createParamDataString, ut_getText, ut_isSpecialPage } from '@base/utils/utils'
import { usePfinderAPI } from '@base/composables/pfinderAPI'
import { useAppIndexStore } from '@base/stores/indexStore'

export const usePathStore = defineStore('pathStore', () => {
  const state: { [key: string]: tp.IBreadCrumbItem[] } = reactive({})

  const indexStore = useAppIndexStore()
  const pfinderAPI = usePfinderAPI()

  function cacheKey(paramData: tp.IParamData): string {
    return [ut_createParamDataString(paramData), paramData.realpath].join('#')
  }

  async function callPathData(paramData: tp.IParamData): Promise<void> {
    const pathData = getPathData(paramData)
    if (!pathData) {
      const args: pf.PfCmd<PfCmdType.PATH>['arg'] = {
        slugPath: !paramData.rawurl ? paramData.slugPath : undefined,
        namePath: paramData.rawurl ? paramData.namePath : undefined,
      }
      const data: tp.IFetchData = {
        paramData,
      }
      const res = await pfinderAPI.fetchData(PfCmdType.PATH, args, data)

      if (res && res?.result.length > 0) {
        setPathData(paramData, res.result)
      }
    }
  }

  function processPathResponse(paramData: tp.IParamData, pathNodes: pf.TPfNode[]): tp.IBreadCrumbItem[] | undefined {
    const pathData: tp.IBreadCrumbItem[] = []
    // Add path and slugPath to nodes
    let namePath: string = ''
    let slugPath: string = ''

    const statePath = !paramData.rawurl ? paramData.slugPath : paramData.namePath
    const pathAr: string[] = statePath ? statePath.split('/') : []

    for (let i = 0; i < pathNodes.length; i++) {
      const node = pathNodes[i]
      if (node) {
        namePath += node.nodeName
        slugPath += node.meta?.slug
        // If embedded in slugPath can be also names
        if (!paramData.rawurl && (node.meta?.slug !== pathAr[i] && node.nodeName !== pathAr[i])) {
          indexStore.setParamDataMutation({ rcco: paramData.rcco, lcda: paramData.lcda, redirectpath: slugPath })
          return
        }
        const bItem: tp.IBreadCrumbItem = Object.assign (node, {
          slugPath: slugPath,
          namePath: namePath,
        })
        pathData.push(bItem)
        if (i < pathNodes.length - 1) {
          namePath += '/'
          slugPath += '/'
        }
      }
    }

    // Check special page
    if (ut_isSpecialPage(pathAr[pathAr.length - 1] || '')) {
      const page: string = pathAr[pathAr.length - 1] || ''
      // Add dummy node
      const bItem: tp.IBreadCrumbItem = {
        attrs: {},
        cod: '',
        desc: ut_getText('def_' + page + '_txt_page', [], paramData), // Here we need special texts
        mediaName: '',
        meta: { slug: page },
        nodeID: -1,
        nodeName: page,
        nodeType: NodeType.nt_None,
        namePath: namePath + '/' + page,
        slugPath: slugPath + '/' + page,
      }
      pathData.push(bItem)
    }

    // Redirect if not all slugs are found
    if (pathAr.length !== pathData.length) {
      indexStore.setParamDataMutation({ rcco: paramData.rcco, lcda: paramData.lcda, redirectpath: slugPath })
      return
    }
    else {
      return pathData
    }
  }

  function setPathData(paramData: tp.IParamData, data: pf.TPfNode[]): tp.IBreadCrumbItem[] | undefined {
    // Process the response, add paths and detect redirect
    const pathData: tp.IBreadCrumbItem[] | undefined = processPathResponse(paramData, data)
    if (pathData) {
      ut_capStore(state)
      state[cacheKey(paramData)] = pathData
    }
    return pathData
  }

  function getPathData(paramData: tp.IParamData): tp.IBreadCrumbItem[] | undefined {
    return state[cacheKey(paramData)]
  }

  // Add to store path after rawUrl modify
  function copyPathData(paramData: tp.IParamData, newRealPath: string): void {
    const pathData = getPathData(paramData)
    if (pathData) {
      const paramData2: tp.IParamData = JSON.parse(JSON.stringify(paramData))
      paramData2.realpath = newRealPath
      ut_capStore(state)
      state[cacheKey(paramData2)] = pathData
    }
  }

  return {
    state,
    callPathData,
    getPathData,
    setPathData,
    copyPathData,
  }
})
